import { CommonModule } from "@angular/common";
import { Component, computed, inject, input } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import {
    DocumentEtcTypeKey,
    DocumentInServiceTypeKey,
    DocumentPhase,
    DocumentPreServiceTypeKey,
    DocumentStatus,
} from "@malirang/common";
import {
    Badge,
    Icon,
    ModalService,
    ScreenSizeService,
} from "@malirang/front-share";
import { DocumentStore } from "../../../store/document.store";
import { DocumentDto } from "@malirang/api-client";
import {
    FixDocumentContentModal,
    FixDocumentContentModalProps,
} from "../../../pages/fix-document/fix-document-content/fix-document-content.modal";

export type DocumentCardParams = {
    required?: boolean;
    name: string;
    value:
        | DocumentPreServiceTypeKey
        | DocumentInServiceTypeKey
        | DocumentEtcTypeKey;
    updatedAt?: Date;
    status: keyof typeof DocumentStatus;
    phase: keyof typeof DocumentPhase;
    document?: DocumentDto;
    lock?: boolean;
};

@Component({
    selector: "app-pre-document-card",
    templateUrl: "./pre-document-card.html",
    styleUrls: ["./pre-document-card.css"],
    imports: [CommonModule, Icon, Badge],
})
export class PreDocumentCard {
    readonly documentStore = inject(DocumentStore);
    readonly router = inject(Router);
    readonly route = inject(ActivatedRoute);
    readonly modalService = inject(ModalService);
    readonly screenSizeService = inject(ScreenSizeService);

    params = input.required<DocumentCardParams>();

    readonly queryParams = toSignal(this.route.queryParams);

    currentDocument = computed(() =>
        this.documentStore
            .preDocuments()
            .find((doc) => doc.preServiceType === this.params().value),
    );

    lock = computed(() => this.params()?.lock);

    isActive = computed(() => {
        const currentType = this.queryParams()?.["type"];
        // URL에 type이 없으면(첫 진입 등), 첫 번째 필수 문서이거나 하는 로직이 있을 수 있지만,
        // 여기서는 명시적으로 type이 일치할 때만 활성화합니다.
        // 단, 문서를 클릭해서 들어간 상태라면 type 파라미터가 있어야 합니다.
        return currentType === this.params().value;
    });

    handleDetail() {
        if (this.lock()) {
            return;
        }

        // 문서가 있으면 해당 문서 ID로, 없으면 type 쿼리 파라미터로 detail 페이지 이동
        const documentId = this.currentDocument()?.id;
        const type = this.params().value;

        // 모바일/태블릿에서는 모달로 표시 (기존 문서만)
        if (this.screenSizeService.isTabletOrMobile() && documentId) {
            this.modalService.create<FixDocumentContentModalProps>(
                FixDocumentContentModal,
                {
                    componentProps: {
                        documentId: documentId,
                    },
                },
            );
            return;
        }

        this.router.navigate(
            [`/document/document-box/pre/${documentId || "new"}`],
            {
                queryParams: {
                    type,
                    phase: "PRE_SERVICE",
                },
                queryParamsHandling: "merge",
                replaceUrl: true,
            },
        );
    }
}
